import { defineStore, storeToRefs } from "pinia";
import { useCookies } from "vue3-cookies";
import axios from 'axios';
import LoginRequest from "@/types/api/post/LoginRequest";
import router from "@/router";
import { toastError } from "@/utils/helpers";
import * as Sentry from "@sentry/vue";
import { useAPIStore } from "./APIStore";
import extractDomain from 'extract-domain'
import { useUIStore } from "./UIStore";
import { env } from "@/config/env";
const { VITE_API_V1_MAIN_DOMAIN, VITE_API_V1_PORT, VITE_API_V1_URL, VITE_API_V1_ROOT_DOMAIN, VITE_PROD } = env;

const { cookies } = useCookies();


export const useAuthStore = defineStore('authStore', {
	state: () => ({
		// Get from cookie instead of localStorage since we have
		// a single sign on system
		user: cookies.get('userDataHttpOnly') ? JSON.parse(atob(cookies.get('userDataHttpOnly'))) : null,

		returnUrl: null,
		authentication: {
			email: null,
			token: null,
			userdomains: [],
		},
		isLoggingOut: false,
		isRequestingElevatedSession: false,
		resolveElevatedSessionPromise: null,
		rejectElevatedSessionPromise: null,
		elevatesSessionData: cookies.get('elevatedSessionDataHttpOnly') ? JSON.parse(atob(cookies.get('elevatedSessionDataHttpOnly'))) : null,
	}),
	actions: {
		isProductionEnvironment() {
			return env.VITE_NODE_ENV === 'production';
		},

		isPublicDomain() {
			return window.location.hostname == VITE_API_V1_MAIN_DOMAIN
		},

		async loginHandler(user, rememberMe) {
			const APIStore = useAPIStore();

			// update pinia state
			this.user = user.data;

			if (rememberMe) {
				// store user details in local storage to keep user logged in between page refreshes
				cookies.set(
					"userDataHttpOnly",
					btoa(JSON.stringify(user.data)),
					'30d',
					"/",
					`.${VITE_API_V1_ROOT_DOMAIN}`
				);
			} else {
				cookies.set(
					"userDataHttpOnly",
					btoa(JSON.stringify(user.data)),
					'0',
					"/",
					`.${VITE_API_V1_ROOT_DOMAIN}`
				);
			}

			if (this.user) {
				if (this.user.userdomains?.length > 0 && !this.user.userdomains.includes(window.location.hostname)) {
					const first_domain = this.user.userdomains[0]
					axios.defaults.baseURL = window.location.protocol + "//" + first_domain + VITE_API_V1_PORT + VITE_API_V1_URL;
				}
				const data = await APIStore.getUserByID(this.user.id)
				if (data) {
					Sentry.setUser({ id: data.id, email: data.email });
				}
			}

			await APIStore.getOrganizationModulePermissions()
			await APIStore.getOrganizationStrategy()
			// The organization was fetched before login (App.vue); fetch it again now
			// that fields served only to authenticated users are included.
			await APIStore.getOrganization()

			// Prefetch the user's other tenants so the "Switch tenant" entry in the
			// profile dropdown shows immediately after login, not only after a
			// refresh (App.vue's onMounted only prefetches these on a full load).
			// Non-blocking: a failure here must not break login — the dropdown
			// lazily retries the fetch when opened.
			APIStore.getMyTenants().catch(() => {})
		},

		async login(loginRequest: LoginRequest, toast: any) {
			const port = VITE_API_V1_PORT || "";
			const formattedPort = (port && !port.startsWith(":")) ? `:${port}` : port;
			const loginUrl = `${window.location.protocol}//${VITE_API_V1_MAIN_DOMAIN}${formattedPort}${VITE_API_V1_URL}login/`;
			const newLoginRequest = { ...loginRequest, app_client: window.location.hostname }
			const user = await axios.post(loginUrl, newLoginRequest);

			await this.loginHandler(user, loginRequest.remember_me)
		},

		async magicLogin(token: string, toast: any) {
			const user = await axios.post(`magic-login/`, { token });
			await this.loginHandler(user, false);
		},

		async loginMicrosoft(code, toast) {
			// the callback page shows the failure in its StatusIndicatorText,
			// so the interceptor must not also raise a toast for it
			const user = await axios.get(`login/microsoft/callback`, {
				params: {
					code: code
				},
				hideToast: true
			});

			this.loginHandler(user, false)
		},

		async logout(redirect = true, reasonIsUpdate = false) {
			const clearAuthCookies = () => {
				// Get all current cookie names from the browser
				const currentCookies = document.cookie.split(';').map(cookie => cookie.split('=')[0].trim());

				// Define the base names of the cookies to destroy
				const targetBases = [
					'csrftoken', 
					'sessionid', 
					'userDataHttpOnly', 
					'elevatedSessionDataHttpOnly'
				];

				// Filter down to only the cookies that start with those base names
				const matchedCookies = currentCookies.filter(cookieName => 
					targetBases.some(base => cookieName.startsWith(base))
				);

				// Loop through and destroy every matched cookie across our domains
				matchedCookies.forEach(cookieName => {
					// Clear from current domain
					cookies.remove(cookieName, "/");
					
					// Clear from root domain
					cookies.remove(cookieName, "/", `.${VITE_API_V1_ROOT_DOMAIN}`);
					
					// Retain your specific logic for the admin subdomain CSRF token
					if (cookieName.startsWith("csrftoken")) {
						cookies.remove(cookieName, "/", `admin.${VITE_API_V1_ROOT_DOMAIN}`);
					}
				});
			};

			try {
				this.isLoggingOut = true
				this.user = null;

				const port = VITE_API_V1_PORT || "";
				const formattedPort = (port && !port.startsWith(":")) ? `:${port}` : port;
				const logoutUrl = `${window.location.protocol}//${VITE_API_V1_MAIN_DOMAIN}${formattedPort}${VITE_API_V1_URL}logout/`

				try {
					// try to logout in the backend
					// this is only possible if the session ID is still valid
					await axios.post(logoutUrl)
				} catch (e) { }

				clearAuthCookies()

				Sentry.setUser(null);

				if (redirect) {
					// Stay on the current tenant's host — logging out should land
					// the user on this tenant's login page, not the first tenant
					// they happen to belong to.
					if (reasonIsUpdate) {
						router.push('/auth/sign-in?reason=update')
					}
					else {
						router.push('/auth/sign-in')
					}
				}

				setTimeout(() => {
					// Give a 2 second frame to actually logout
					this.isLoggingOut = false
				}, 2000)

			} catch (e) {
				router.push('/auth/sign-in')
			}
		},

		async signWithElevatedSession(rb = null) {
			if (rb == null) {
				rb = {}
			}

			const APIStore = useAPIStore()
			const { profile } = storeToRefs(APIStore)

			if (profile.value.microsoft_sso_user) {
				return { ...rb, elevated_privileges_token: null }
			}

			let elevatedSessionData = null
			if (cookies.get('elevatedSessionDataHttpOnly')) {
				elevatedSessionData = JSON.parse(atob(cookies.get('elevatedSessionDataHttpOnly')))
			}
			if (!elevatedSessionData && this.isProductionEnvironment()) {
				const uiStore = useUIStore()
				this.isRequestingElevatedSession = true
				uiStore.enableElevatedSessionPopup()

				const data = await new Promise((resolve, reject) => {
					this.resolveElevatedSessionPromise = resolve;
					this.rejectElevatedSessionPromise = reject;
				})

				elevatedSessionData = data
			}
			return { ...rb, elevated_privileges_token: elevatedSessionData?.key }
		},

		async openElevatedSession(password, toast) {
			if (!this.resolveElevatedSessionPromise) {
				throw new Error("There was no signing session to be signed!")
			}

			const { data } = await axios.post('request-elevated-privileges/', {
				password: password,
				duration: 0,
			})

			const uiStore = useUIStore()

			cookies.set("elevatedSessionDataHttpOnly", btoa(JSON.stringify(data)), 300, '/', `.${VITE_API_V1_ROOT_DOMAIN}`)
			this.elevatesSessionData = data
			this.isRequestingElevatedSession = false
			uiStore.disableElevatedSessionPopup(false)
			try {
				this.resolveElevatedSessionPromise(data)
				this.removeElevatedSessionPromiseManagers()
			} catch (e) { }
			return data
		},

		removeElevatedSessionPromiseManagers() {
			this.resolveElevatedSessionPromise = null
			this.rejectElevatedSessionPromise = null
		},

		async rejectElevatedSession() {
			this.rejectElevatedSessionPromise()
			this.removeElevatedSessionPromiseManagers()
		},

		async closeElevatedSession() {
			const uiStore = useUIStore()
			this.elevatesSessionData = null
			cookies.remove("elevatedSessionDataHttpOnly", "/", `.${VITE_API_V1_ROOT_DOMAIN}`)
			uiStore.disableElevatedSessionPopup(false)
		},

		async handleRedirectDomain(endpoint: string = '/', force: boolean = true, bypassPublicPageGuard: boolean = false) {
			const allowedPublicPages = [
				'/auth/reset-password',
				'/auth/sign-in/trouble/forgot-password',
				// The sign-up route is also where existing users *accept* an
				// invitation to join a new tenant. The invitation host is by
				// definition NOT yet in `user.userdomains`, so without this
				// exclusion the userdomain-mismatch branch below would yank
				// the user back to their first existing tenant and they'd
				// lose the invitation context entirely.
				'/auth/sign-up'
			]

			const onAllowedPublicPage = (page) => {
				return allowedPublicPages.some(p => page.includes(p))
			}

			// Don't perform any redirect (cross-domain or in-app) while the
			// user is on a page that legitimately needs to stay where it is.
			// Explicit user-initiated navigations (e.g. "Go to portal" after
			// completing the sign-up flow) opt out via `bypassPublicPageGuard`.
			if (!bypassPublicPageGuard && onAllowedPublicPage(router.currentRoute.value.path)) {
				return
			}

			if (this.user) {
				if (this.user.userdomains?.length > 0 && !this.user.userdomains.includes(window.location.hostname)) {
					let currentport = ''
					if (window.location.port) {
						currentport = `:${window.location.port}`
					}
					const first_domain = this.user.userdomains[0]
					const url = `${window.location.protocol}//${first_domain}${currentport}${endpoint}`

					// for security reasons double check that the redirect url is not a malicious site
					const domain = extractDomain(url)
					if (domain == VITE_API_V1_ROOT_DOMAIN) {
						window.location.href = url
					}
				} else if (force) {
					// `onAllowedPublicPage` is already guarded by the early return at
					// the top of this function, so we can push unconditionally here.
					await router.push(endpoint);
				}
			}
		}
	}
})
